import { ChangeEventHandler, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Input, Datepicker, RangeDatepicker, SelectOption, SelectProps } from '@ets/design-system';

import { dayjs } from 'utils';

import { GENERIC_BEHAVIOUR, HandlerProps } from '../AdvancedFilterOptions';
import { useAdvancedFilterContext } from '../AdvancedFilterContext';
import { PortalSelect } from '../PortalSelect';

import styles from './Handlers.module.scss';
import { useState } from 'react';
import { useDebounce, useEffectOnUpdate } from '_common/hooks';

type GenericHandlerProps = {
  testId?: string;
};

const GenericHandler = ({ sequence, testId }: HandlerProps & GenericHandlerProps) => {
  const intl = useIntl();

  const { advancedFilter, setOperator, setValue, setAuxValue, setQuery } =
    useAdvancedFilterContext();

  const { option, metadataOption, operator, value, auxValue } = advancedFilter;
  const mergedOption = metadataOption ? `${option}.${metadataOption}` : option;

  const [localValue, setLocalValue] = useState<string>();
  const debouncedValue = useDebounce(localValue, 300);

  useEffectOnUpdate(() => {
    if (value !== debouncedValue) {
      setValue(debouncedValue);
    }
  }, [debouncedValue]);

  useEffect(() => {
    setLocalValue(value);
    if (!sequence.type || !operator || !value) {
      setQuery(undefined);
      return;
    }

    //Date's Exactly operator will be transformed into a Between
    if (sequence.type === 'date' && operator === 'eq') {
      setQuery(
        GENERIC_BEHAVIOUR[sequence.type]['in']({
          field: mergedOption,
          value: value,
          auxValue: dayjs(value).add(1, 'day').toISOString(),
        }),
      );
    } else if (operator !== 'in') {
      setQuery(
        GENERIC_BEHAVIOUR[sequence.type][operator]({
          field: mergedOption,
          value: value,
        }),
      );
    } else if (auxValue) {
      setQuery(
        GENERIC_BEHAVIOUR[sequence.type][operator]({
          field: mergedOption,
          value: value,
          auxValue: auxValue,
        }),
      );
    }
  }, [value, auxValue]);

  // This should never happen since 'sequence' is only non-undefined when option is selected
  // and 'sequence' is a required prop for this component, but double checking
  if (!sequence || !advancedFilter.option) {
    return null;
  }

  const handleStringChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setLocalValue(event.target.value);
  };

  const handleDateChange = (date: Date | null) => {
    setValue(date?.toISOString());
  };

  const handleAuxDateChange = (date: Date | null) => {
    setAuxValue(date?.toISOString());
  };

  const handleOperatorChange: SelectProps<SelectOption>['onChange'] = (newSelectValue) => {
    setOperator(newSelectValue?.value);
  };

  const renderValueInput = () => {
    // Only render input if an operator has been chosen
    if (!operator) {
      return null;
    }

    switch (sequence.type) {
      case 'string':
        return (
          <Input
            size="m"
            placeholder={intl.formatMessage({ id: 'TYPE_HERE' })}
            onChange={handleStringChange}
            value={localValue}
            testId={`${testId}-value`}
          />
        );
      case 'date':
        return operator === 'in' ? (
          <div>
            <RangeDatepicker
              size="m"
              onStartChange={handleDateChange}
              onEndChange={handleAuxDateChange}
              vertical={false}
              startDate={value && dayjs(value).isValid() ? dayjs(value).toDate() : undefined}
              endDate={auxValue && dayjs(auxValue).isValid() ? dayjs(auxValue).toDate() : undefined}
              testId={`${testId}`}
              popperProps={{
                strategy: 'fixed',
              }}
            />
          </div>
        ) : (
          <Datepicker
            size="m"
            onChange={handleDateChange}
            selected={value && dayjs(value).isValid() ? dayjs(value).toDate() : undefined}
            testId={`${testId}`}
            popperProps={{
              strategy: 'fixed',
            }}
          />
        );
    }
  };

  return (
    <div className={styles.handlerRoot} data-testid={testId}>
      <PortalSelect
        width="20rem"
        options={sequence.options}
        onChange={handleOperatorChange}
        value={sequence.options.find((seqOperator) => seqOperator.value === operator) || null} //Force select UI cleanup by sending null
        testId={`${testId}-operator`}
      />

      {renderValueInput()}
    </div>
  );
};

export default GenericHandler;
